import { useSelector } from 'react-redux';
import PreferredZipCodeSelector from 'selectors/user/preferredZipCodeSelector';
import { shipToHomeSelector } from 'selectors/page/product/fulfillmentOptions/deliveryOptions/shipToHome/shipToHomeSelector';
import skuUtils from 'utils/Sku';

const { preferredZipCodeSelector } = PreferredZipCodeSelector;

/**
 * useFulfillmentMethodsProps
 *
 * Reads the Redux slices the SHP-5111 Fulfillment Methods orchestrator needs
 * to render the default state: the user's preferred ZIP code (for the details
 * panel chevron) and the ship-to-home message (for the Free Shipping card ETA
 * and the details panel green ETA text).
 *
 * SHP-5122 additionally derives Auto-Replenish eligibility so the orchestrator
 * can decide whether to render the AR checkbox row at all and whether it should
 * be interactive. The derivation mirrors `useDeliveryOptionsProps` so the
 * redesign and the legacy layout stay in lockstep:
 * `isReplenishmentEligible` reflects the current SKU, while
 * `showAutoReplenishment` is gated by product-level eligibility across regular
 * child SKUs plus the `isAutoReplenishmentEnabled` config flag.
 *
 * `preferredZipCodeSelector` always returns a string (falls back to
 * `Empty.String` when the user has not set a location), so `preferredZipCode`
 * is never undefined. `shipToHomeSelector` always returns an object (falls
 * back to `Empty.Object`), but `shipToHomeMessage` inside that object can
 * still be undefined until the shipping data loads.
 *
 * SHP-5234 adds `isGiftCard`, derived from the current SKU via `skuUtils.isGiftCard`
 * (mirrors `useDeliveryOptionsProps.js`), so the orchestrator can render gift-card
 * PDPs with Fast Delivery/Pickup shown as non-interactive, informational cards.
 *
 * @param {object} [currentProduct] - The current product from Redux.
 * @returns {{
 *   preferredZipCode: string,
 *   shipToHomeMessage: string | undefined,
 *   showAutoReplenishment: boolean,
 *   isReplenishmentEligible: boolean,
 *   isGiftCard: boolean
 * }}
 */
export default function useFulfillmentMethodsProps(currentProduct) {
    const preferredZipCode = useSelector(preferredZipCodeSelector);
    const shipToHome = useSelector(shipToHomeSelector);

    const currentSku = currentProduct?.currentSku;
    const isReplenishmentEligible = Boolean(currentSku?.isReplenishmentEligible);
    const autoReplenProductEligibility =
        currentProduct?.regularChildSkus?.length > 0
            ? currentProduct.regularChildSkus.some(element => element?.isReplenishmentEligible)
            : isReplenishmentEligible;
    const showAutoReplenishment = Boolean(autoReplenProductEligibility && Sephora.configurationSettings.isAutoReplenishmentEnabled);
    const isGiftCard = Boolean(currentSku && skuUtils.isGiftCard(currentSku));

    return {
        preferredZipCode,
        shipToHomeMessage: shipToHome.shipToHomeMessage,
        showAutoReplenishment,
        isReplenishmentEligible,
        isGiftCard
    };
}
